import React, { useState, useImperativeHandle, useEffect, useRef } from 'react';
import { Fade, Box } from '@mui/material';
import omit from 'lodash-es/omit';
import clsx from 'clsx';
import { ToastComponent } from './ToastComponent';
import { getToastListStyle, toastClasses, commonOffsetSize } from './style';
function getTextAlign(config) {
    let textAlign = 'center';
    if (typeof config?.placement === 'string') {
        if (config.placement.endsWith?.('-start')) {
            textAlign = 'left';
        }
        else if (config.placement.endsWith?.('-end')) {
            textAlign = 'right';
        }
    }
    return textAlign;
}
function getPlacementClassName(config) {
    if (config?.placement === 'top-start') {
        return toastClasses.topStart;
    }
    if (config?.placement === 'top-end') {
        return toastClasses.topEnd;
    }
    if (config?.placement === 'bottom') {
        return toastClasses.bottom;
    }
    if (config?.placement === 'bottom-start') {
        return toastClasses.bottomStart;
    }
    if (config?.placement === 'bottom-end') {
        return toastClasses.bottomEnd;
    }
    return toastClasses.top;
}
const filterToastDataList = (dataList, dataConfig) => {
    const newDataList = [];
    for (let i = 0; i < dataList.length; i += 1) {
        const data = dataList[i];
        if (!data?.isHidden) {
            newDataList.push(data);
        }
    }
    if (dataConfig.maxCount !== undefined &&
        dataConfig.maxCount > 0 &&
        newDataList.length + 1 >= dataConfig.maxCount) {
        newDataList.splice(0, newDataList.length - dataConfig.maxCount + 1);
    }
    return newDataList;
};
// It mainly merges the existing and incremental toast data,
// deletes the toasts with duplicate keys,
// and only retains the latest toast data with unique keys.
const mergeToastDataList = (originList, newList) => {
    const originKeys = originList.map((item) => item.key);
    const appendList = [];
    newList.forEach((item) => {
        const existOriginKeyIndex = originKeys.indexOf(item.key);
        // Avoid duplicate keys in the original toast data
        if (existOriginKeyIndex >= 0) {
            originList.splice(existOriginKeyIndex, 1, item);
        }
        else {
            const existAppendKeyIndex = appendList.findIndex((i) => i.key === item.key);
            // Avoid duplicate keys in the appended toast data
            if (existAppendKeyIndex >= 0) {
                appendList.splice(existAppendKeyIndex, 1, item);
            }
            else {
                appendList.push(item);
            }
        }
    });
    const resultList = [...originList, ...appendList];
    return resultList;
};
const filterBufferedDataList = (renderList, currentToastList) => {
    const bufferedList = [];
    const currentToastKeys = currentToastList.map((item) => item.key);
    renderList.forEach((item) => {
        if (!currentToastKeys.includes(item.key)) {
            bufferedList.push(item);
        }
    });
    return bufferedList;
};
export const ToastList = React.forwardRef((props, ref) => {
    const { defaultConfig, handlerRef, hasContainer } = props;
    const [config, setConfig] = useState(defaultConfig);
    const [toastDataList, setToastDataList] = useState([]);
    const internalRef = useRef(null);
    const refBufferedToastDataList = useRef([]);
    useEffect(() => {
        // When toastDataList is actually updated, the buffered data will be cleared.
        refBufferedToastDataList.current = [];
        const listContainer = internalRef.current;
        if (listContainer.classList.contains(toastClasses.scroll)) {
            listContainer.classList.remove(toastClasses.scroll);
        }
        if (listContainer.clientHeight + commonOffsetSize > window.innerHeight) {
            listContainer.classList.add(toastClasses.scroll);
        }
    }, [toastDataList]);
    useImperativeHandle(ref, () => internalRef.current, []);
    useImperativeHandle(handlerRef, () => {
        const handler = {
            updateConfig(newConfig) {
                setConfig({ ...config, ...newConfig });
            },
            appendToasts(dataList) {
                const renderDataList = dataList.map((data) => ({
                    ...data,
                    isHidden: false,
                    canBeDeleted: false,
                }));
                // Filter the dataList to be rendered and limit the number according to maxCount
                const filteredDataList = filterToastDataList([...toastDataList, ...refBufferedToastDataList.current], config);
                // It mainly merges the existing and incremental toast data,
                // deletes the toasts with duplicate keys,
                // and only retains the latest toast data with unique keys.
                const mergedDataList = mergeToastDataList(filteredDataList, renderDataList);
                // If the setState method cannot keep up with the re-render when the ref method is executed frequently, the newly added data of toastDataList needs to be saved and cached.
                // When toastDataList is actually updated, the buffered data will be cleared.
                // It needs to put the new dataList into the buffer.
                refBufferedToastDataList.current = filterBufferedDataList(mergedDataList, toastDataList);
                setToastDataList([...mergedDataList]);
            },
            closeToast(key) {
                let index = -1;
                for (let i = 0; i < toastDataList.length; i += 1) {
                    if (toastDataList[i]?.key === key) {
                        index = i;
                        break;
                    }
                }
                if (index >= 0) {
                    if (toastDataList[index]?.isHidden) {
                        return;
                    }
                    toastDataList[index].isHidden = true;
                    toastDataList[index]?.onClose?.();
                    setToastDataList([...toastDataList]);
                }
            },
            destroyToast(key) {
                let index = -1;
                for (let i = 0; i < toastDataList.length; i += 1) {
                    if (toastDataList[i]?.key === key) {
                        index = i;
                        break;
                    }
                }
                if (index >= 0) {
                    toastDataList.splice(index, 1);
                    setToastDataList([...toastDataList]);
                }
            },
            updateToast(key, options) {
                const opts = omit(options, ['key', 'duration']);
                for (let i = 0; i < toastDataList.length; i += 1) {
                    // istanbul ignore else
                    if (toastDataList[i]?.key === key) {
                        toastDataList[i] = {
                            ...toastDataList[i],
                            ...opts,
                            ...{ isHidden: false, canBeDeleted: false },
                        };
                        break;
                    }
                }
                setToastDataList([...toastDataList]);
            },
        };
        return handler;
    }, [toastDataList, config]);
    const afterFadeExit = (key) => {
        let index = -1;
        for (let i = 0; i < toastDataList.length; i += 1) {
            // istanbul ignore else
            if (toastDataList[i]?.key === key) {
                index = i;
                break;
            }
        }
        // istanbul ignore else
        if (index >= 0) {
            toastDataList[index].canBeDeleted = true;
            setToastDataList([...toastDataList]);
        }
    };
    return (React.createElement(Box, { ...config.listProps, className: clsx(toastClasses.list, getPlacementClassName(config)), sx: [
            (theme) => getToastListStyle({ config, theme, hasContainer }),
            ...(config.listProps?.sx && Array.isArray(config.listProps?.sx)
                ? config.listProps.sx
                : [config.listProps?.sx]),
        ], ref: internalRef }, toastDataList.map((data) => {
        const { key, className, type, isHidden, canBeDeleted, closable, actionable, onCancel, onOk, cancelText, okText, reverseButtons, okButtonProps, cancelButtonProps, icon, closeButtonProps, titleProps, contentProps, rootProps, screenReadOnly, width, maxWidth, } = data;
        if (canBeDeleted) {
            return null;
        }
        return (React.createElement(Box, { key: key, className: clsx(toastClasses.item, {
                [toastClasses.screenReadOnly]: screenReadOnly,
            }), style: {
                textAlign: getTextAlign(config),
            } },
            React.createElement(Fade, { in: !isHidden, timeout: 500, onExited: () => {
                    afterFadeExit(key);
                } },
                React.createElement(Box, { style: { position: 'relative', display: 'inline-block' } },
                    React.createElement(ToastComponent, { toastKey: key, className: className, type: type, title: data.title, content: data.content, closable: closable, icon: icon, afterClose: (toastKey) => {
                            handlerRef?.current?.closeToast?.(toastKey);
                        }, actionable: actionable, onCancel: onCancel, onOk: onOk, cancelText: cancelText, okText: okText, reverseButtons: reverseButtons, okButtonProps: okButtonProps, cancelButtonProps: cancelButtonProps, closeButtonProps: closeButtonProps, titleProps: titleProps, contentProps: contentProps, rootProps: rootProps, screenReadOnly: screenReadOnly, width: width, maxWidth: maxWidth })))));
    })));
});
